import { Alert, Switch, Table, Tag, Typography, message } from 'antd';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useModules } from '../contexts/ModulesContext';
import { canToggleModule, type ModuleState, moduleTitleKey } from '../domain/modules';
import useRequest from '../hooks/useRequest';

const { Paragraph } = Typography;

/**
 * The administrator's view of the separately sold modules: whether each is licensed (read-only,
 * set by the supplier) and the switch that turns a licensed module on or off for this shop.
 */
export default function ApplicationModules() {
    const { t } = useTranslation();
    const request = useRequest();
    const { modules, loaded, reload } = useModules();
    const [savingKey, setSavingKey] = useState<string | null>(null);

    const switchModule = async (module: ModuleState, active: boolean) => {
        setSavingKey(module.key);
        try {
            await request({
                path: `/modules/${encodeURIComponent(module.key)}/active`,
                method: 'PUT',
                params: { active },
            });
            await reload();
            message.success(t(active ? 'modules.activated' : 'modules.deactivated'));
        } catch (error) {
            console.error('Switching a module failed:', error);
            message.error(t('modules.switchFailed'));
        } finally {
            setSavingKey(null);
        }
    };

    const columns = [
        {
            title: t('modules.columns.module'),
            key: 'module',
            render: (_: unknown, module: ModuleState) => t(moduleTitleKey(module.key)),
        },
        {
            title: t('modules.columns.licence'),
            key: 'licence',
            render: (_: unknown, module: ModuleState) =>
                module.licensed ? (
                    <Tag color="green">{t('modules.licensed')}</Tag>
                ) : (
                    <Tag>{t('modules.notLicensedShort')}</Tag>
                ),
        },
        {
            title: t('modules.columns.active'),
            key: 'active',
            render: (_: unknown, module: ModuleState) => (
                <Switch
                    data-testid={`module-switch-${module.key}`}
                    checked={module.active}
                    disabled={!canToggleModule(module)}
                    loading={savingKey === module.key}
                    onChange={checked => void switchModule(module, checked)}
                />
            ),
        },
    ];

    return (
        <div data-testid="application-modules">
            <Paragraph type="secondary">{t('modules.description')}</Paragraph>
            {loaded && modules.length === 0 && (
                <Alert type="warning" showIcon message={t('modules.loadFailed')} />
            )}
            <Table
                rowKey="key"
                size="small"
                pagination={false}
                loading={!loaded}
                dataSource={modules}
                columns={columns}
            />
        </div>
    );
}
